import { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  Route,
  useHistory,
  useLocation,
  Redirect,
  Switch,
} from "react-router-dom";
import * as microsoftTeams from "@microsoft/teams-js";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { useIsAuthenticated, useMsal, MsalProvider } from "@azure/msal-react";
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { AedasButton } from "./Components/Shared/AedasButton/AedasButton";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { validatePermission } from "./Utils/permissionsUtils";
import { loginRequest } from "./authConfig";
import { CustomNavigationClient } from "./Utils/NavigationClient";
import { AedasLoading } from "./Components/Shared/AedasLoading/AedasLoading";
import { Header } from "./Components/Shared/Header/Header";
import { userLogin } from "./Redux/Actions/User.action";
import { IMenu } from "./interface/header";
import { loginAzure, loginTeams } from "./Services/AuthService";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { getAllInternalPositions } from "./Services/InternalPosition";
import { getPositions } from "./Redux/Actions/Positions.action";
import { getAllLocations } from "./Services/LocationService";
import { getLocations } from "./Redux/Actions/Locations.action";
import { getBackendErrorMessage } from "./Utils/backendError";
import {
  addLoginAttempt,
  clearStaleMsalState,
  getLoginAttempts,
  isJwtExpired,
  MAX_LOGIN_ATTEMPTS,
  resetLoginAttempts,
  restartLogin,
} from "./Utils/authRecovery";

// Si la pantalla de carga dura más que esto, se ofrece volver a iniciar
// sesión (antes la única salida era borrar caché y cookies a mano)
const SLOW_LOGIN_MS = 20000;

// Fuera de Teams initialize() puede no resolver ni rechazar nunca
const TEAMS_INIT_TIMEOUT_MS = 3000;

// Cada página de ruta se carga en su propio chunk: App.tsx dejaba de otro
// modo un único bundle con todas las páginas (organigramas, borradores,
// históricos, gestión de empleados) descargado de golpe en el primer load.
const Login = lazy(() =>
  import("./Components/Static/Login/Login").then((m) => ({
    default: m.Login,
  })),
);
const OrganizationChart = lazy(() =>
  import("./Components/Static/OrganizationChartMain/OrganizationChart").then(
    (m) => ({ default: m.OrganizationChart }),
  ),
);
const CurrentOrgChart = lazy(() =>
  import(
    "./Components/Static/OrganizationChartMain/ShowCurrent/CurrentOrgChart"
  ).then((m) => ({ default: m.CurrentOrgChart })),
);
const ShowDrafts = lazy(() =>
  import(
    "./Components/Static/OrganizationChartMain/ShowDrafts/ShowDrafts"
  ).then((m) => ({ default: m.ShowDrafts })),
);
const EditDraftOrgChart = lazy(() =>
  import(
    "./Components/Static/OrganizationChartMain/ShowDrafts/EditDraftOrgChart/EditDraftOrgChart"
  ).then((m) => ({ default: m.EditDraftOrgChart })),
);
const HistoryOrganizationChart = lazy(() =>
  import(
    "./Components/Static/OrganizationChartMain/ShowHistory/HistoryOrgChart"
  ).then((m) => ({ default: m.HistoryOrganizationChart })),
);
const ShowOrgChartHistory = lazy(() =>
  import(
    "./Components/Static/OrganizationChartMain/ShowHistory/History/ShowOrgChartHistory"
  ).then((m) => ({ default: m.ShowOrgChartHistory })),
);
const EmployeeManagementMain = lazy(() =>
  import("./Components/Static/EmployeeManagement/EmployeeManagementMain").then(
    (m) => ({ default: m.EmployeeManagementMain }),
  ),
);
const Tab = lazy(() =>
  import("./Visual/components/Tab/Tab").then((m) => ({ default: m.Tab })),
);
const OrgChartCompany = lazy(() =>
  import("./Visual/components/Organigrama/OrganigramaCompany").then((m) => ({
    default: m.OrgChartCompany,
  })),
);
const OrgChartDepartment = lazy(() =>
  import("./Visual/components/Organigrama/OrgChartDepartment").then((m) => ({
    default: m.OrgChartDepartment,
  })),
);
const OrganigramaMainTag = lazy(() =>
  import("./Visual/components/Organigrama/OrgChartTag").then((m) => ({
    default: m.OrganigramaMainTag,
  })),
);

type AppProps = {
  pca: IPublicClientApplication;
};

export function App({ pca }: AppProps) {
  // Aplicacion de teams

  const history = useHistory();
  const navigationClient = new CustomNavigationClient(history);
  pca.setNavigationClient(navigationClient);

  return (
    <>
      <ToastContainer autoClose={1500}></ToastContainer>
      <MsalProvider instance={pca}>
        <Router></Router>
      </MsalProvider>
    </>
  );
}

const Router = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const isLogin = useSelector((state: any) => state.userReducer.isLogin);
  const user = useSelector((state: any) => state.userReducer.user);
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useDispatch();
  const [token, setToken] = useState<boolean>(false);
  // Motivo por el que no se ha podido entrar; con él, en vez de la pantalla
  // de carga infinita se muestra el aviso con el botón para reintentar
  const [authError, setAuthError] = useState<string | null>(null);
  const [slowLogin, setSlowLogin] = useState(false);

  useEffect(() => {
    if (token) return;
    const timer = setTimeout(() => setSlowLogin(true), SLOW_LOGIN_MS);
    return () => clearTimeout(timer);
  }, [token]);

  // El backend ha rechazado el acceso: se descarta la sesión anterior
  // guardada en el navegador ("aedas_user"). Si no, con un "aedas_user"
  // caducado la app se pintaba igualmente (token = true), la pantalla de
  // error nunca aparecía y todo se quedaba cargando para siempre.
  const failLogin = (message: string) => {
    localStorage.removeItem("aedas_user");
    setToken(false);
    setAuthError(message);
    setIsLoading(false);
  };

  // Login por redirección a Microsoft, con tope de intentos por pestaña para
  // no entrar en bucle si Azure AD sigue fallando
  const redirectToLogin = (request: any, retryingStale = false) => {
    if (getLoginAttempts() >= MAX_LOGIN_ATTEMPTS) {
      setAuthError(
        "No se ha podido iniciar sesión con tu cuenta de Microsoft después de varios intentos."
      );
      return;
    }
    addLoginAttempt();
    const redirect = request.account
      ? instance.acquireTokenRedirect(request)
      : instance.loginRedirect(request);
    redirect.catch((e: any) => {
      console.error(e);
      // Un login anterior quedó a medias y MSAL sigue creyendo que hay uno
      // en curso (inProgress ya es "none", así que no es verdad): se limpia
      // ese estado y se reintenta una vez
      if (
        !retryingStale &&
        e instanceof BrowserAuthError &&
        e.errorCode === "interaction_in_progress"
      ) {
        clearStaleMsalState();
        redirectToLogin(request, true);
        return;
      }
      setAuthError("No se ha podido abrir el inicio de sesión de Microsoft.");
    });
  };

  // Un 401 de cualquier llamada (salvo las del propio login) significa que el
  // "aedas_user" guardado ya no vale: se descarta y se vuelve a pedir el login.
  // Se guarda la última versión de redirectToLogin porque el interceptor se
  // registra una sola vez.
  const redirectToLoginRef = useRef(redirectToLogin);
  redirectToLoginRef.current = redirectToLogin;
  useEffect(() => {
    let handling401 = false;
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url: string = error?.config?.url ?? "";
        if (
          error?.response?.status === 401 &&
          !url.includes("/loginAzure") &&
          !url.includes("/loginTeams") &&
          !handling401
        ) {
          handling401 = true;
          localStorage.removeItem("aedas_user");
          setToken(false);
          redirectToLoginRef.current(loginRequest);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("aedas_user") as string);
    if (stored?.token) {
      // Un "aedas_user" caducado o ilegible no sirve: se descarta y sigue el
      // flujo normal de login
      if (isJwtExpired(stored.token)) {
        localStorage.removeItem("aedas_user");
      } else {
        setToken(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin]);

  useEffect(() => {
    if (!token) {
      return;
    }
    getAllInternalPositions()
      .then((response: any) => {
        dispatch(getPositions(response));
      })
      .catch((error) => console.log("ERROR:", error));
    getAllLocations()
      .then((response: any) => {
        dispatch(getLocations(response));
      })
      .catch((error) => console.log("ERROR:", error));
  }, [token]);

  useEffect(() => {
    if (isLogin) {
      if (
        !validatePermission(
          user ? user.permissions : [],
          "app_organigrama.rrcc.all"
        )
      ) {
        const infoLocation = location.pathname + location.search;

        if (location.pathname.includes("/organigrama/")) {
          history.push(location.pathname);
        } else if (infoLocation.includes("/organigrama?search")) {
          history.push(location.pathname + location.search);
        } else {
          history.push("/organigrama");
        }
      } else {
        if (location.pathname === "/login") {
          history.push("/organigramas");
        } else {
          history.push(location.pathname);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin]);

  useEffect(() => {
    Promise.race([
      microsoftTeams.app.initialize(),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Teams initialize timeout")),
          TEAMS_INIT_TIMEOUT_MS
        )
      ),
    ])
      .then(async () => {
        //Perform Azure AD single sign-on authentication
        microsoftTeams.authentication
          .getAuthToken()
          .then(async (result) => {
            let loginError: any = null;
            const responseBackend: any = await loginTeams(result).catch(
              (error) => {
                loginError = error;
              }
            );
            let position: string = "Por defecto";

            if (responseBackend?.data?.token) {
              dispatch(
                userLogin(
                  responseBackend.data,
                  responseBackend.data.token,
                  position,
                  true
                )
              );
              localStorage.setItem(
                "aedas_user",
                JSON.stringify(responseBackend.data)
              );
              setIsLoading(false);
            } else {
              failLogin(
                getBackendErrorMessage(
                  loginError,
                  "No se ha podido entrar con tu cuenta de Teams."
                )
              );
            }
          })

          .catch((error: any) => {
            // Antes solo se mostraba un aviso y la app se quedaba cargando
            failLogin(
              getBackendErrorMessage(
                error,
                "No se ha podido obtener tu sesión de Teams."
              )
            );
          });
      })
      .catch((error: any) => {
        if (isAuthenticated && inProgress === "none") {
          const account = instance.getActiveAccount() ?? accounts[0];
          const request = {
            ...loginRequest,
            account,
          };
          instance
            .acquireTokenSilent(request) // Consigue el token de forma silenciosa
            .then(async (response) => {
              let loginError: any = null;
              const responseBackend: any = await loginAzure(
                response.accessToken
              ).catch((error) => {
                console.log("ERROR:", error);
                loginError = error;
              });

              let position: string = "Por defecto";

              if (responseBackend?.data?.token) {
                resetLoginAttempts();
                dispatch(
                  userLogin(
                    responseBackend.data,
                    responseBackend.data.token,
                    position,
                    true
                  )
                );
                localStorage.removeItem("aedas_user");
                localStorage.setItem(
                  "aedas_user",
                  JSON.stringify(responseBackend.data)
                );
                setIsLoading(false);
              } else {
                // Antes se iba a /login, pero sin "aedas_user" la pantalla
                // de carga tapaba el login y se quedaba cargando para siempre
                const account = response.account?.username ?? "";
                failLogin(
                  `${getBackendErrorMessage(
                    loginError,
                    "No se ha podido comprobar tu acceso al organigrama."
                  )}${account ? ` (cuenta ${account}).` : ""}`
                );
              }
            })
            .catch((e) => {
              console.log(e);
              // La cuenta guardada en el navegador ya no vale (sesión
              // caducada, contraseña cambiada...): hay que volver a pasar
              // por Microsoft. Antes el error se ignoraba y la app se
              // quedaba cargando para siempre.
              if (e instanceof InteractionRequiredAuthError) {
                redirectToLogin(request);
              } else {
                failLogin(
                  "No se ha podido recuperar tu sesión de Microsoft guardada en este navegador."
                );
              }
            });
        } else if (!isAuthenticated && inProgress === "none") {
          redirectToLogin(loginRequest);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress]);

  const menu: IMenu[] = [
    {
      name: "Organigrama General",
      localPath: "/organigrama",
      icon: <AccountTreeOutlinedIcon />,
      onClick: history.push,
      shortName: "Organigrama",
      canView: validatePermission(
        user ? user.permissions : [],
        "app_organigrama.rrcc.all"
      ),
    },
    {
      name: "Gestión de Organigramas",
      localPath: "/organigramas",
      icon: <AssignmentIndOutlinedIcon />,
      onClick: history.push,
      shortName: "Gestión Org.",
      canView: validatePermission(
        user ? user.permissions : [],
        "app_organigrama.rrcc.all"
      ),
    },
    {
      name: "Gestión de Empleados",
      localPath: "/gestion-empleados",
      icon: <Brightness7Icon />,
      onClick: history.push,
      shortName: "Gestión Emp.",
      canView: validatePermission(
        user ? user.permissions : [],
        "app_organigrama.rrcc.all"
      ),
    },
  ];
  if (authError) {
    return (
      <div
        className="w-100 h-100 d-flex flex-column justify-content-center align-items-center text-center"
        style={{
          gap: "16px",
          padding: "16px",
          // h-100 no basta: el contenedor padre no tiene altura propia
          minHeight: "100vh",
        }}
      >
        <h2>No se ha podido iniciar sesión</h2>
        <p style={{ maxWidth: "480px" }}>
          {authError} Pulsa el botón para cerrar la sesión guardada en este
          navegador y volver a entrar con tu cuenta de AEDAS.
        </p>
        <p style={{ maxWidth: "480px" }}>
          Si vuelve a aparecer este aviso con tu cuenta de AEDAS, tu usuario
          no está dado de alta en el organigrama o no tiene permisos: avisa al
          equipo de soporte.
        </p>
        <AedasButton onClick={() => restartLogin(instance)}>
          Volver a iniciar sesión
        </AedasButton>
      </div>
    );
  }

  if (isLoading || !token) {
    return (
      <div className="w-100 h-100 d-flex flex-column justify-content-center align-items-center">
        <AedasLoading></AedasLoading>
        {slowLogin && (
          <div className="text-center" style={{ padding: "16px" }}>
            <p>¿Tarda demasiado en entrar?</p>
            <AedasButton onClick={() => restartLogin(instance)}>
              Volver a iniciar sesión
            </AedasButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="row" style={{ height: "100vh", overflowX: "hidden" }}>
      {isLogin &&
      validatePermission(
        user ? user.permissions : [],
        "app_organigrama.rrcc.all"
      ) ? (
        <Header menu={menu} open={isOpen} setIsOpen={setIsOpen}></Header>
      ) : (
        <></>
      )}

      <div
        style={
          isLogin &&
          validatePermission(
            user ? user.permissions : [],
            "app_organigrama.rrcc.all"
          )
            ? {
                flex: 1,
                marginLeft: isOpen ? "300px" : "70px",
                maxWidth: "100vw",
                height: "100vh",
                overflowY: "scroll",
                // overflow: "hidden"
              }
            : {
                flex: 1,
                maxWidth: "100vw",
                height: "100vh",
                overflowY: "scroll",
              }
        }
        className="w-100"
      >
        <Suspense
          fallback={
            <div className="w-100 h-100 d-flex justify-content-center">
              <AedasLoading></AedasLoading>
            </div>
          }
        >
        <Switch>
          <Route exact path="/login">
            <Login isLogin={isLogin}></Login>
          </Route>

          <PrivateRoute
            auth={isLogin}
            hasPermissions={validatePermission(
              user ? user.permissions : [],
              "app_organigrama.rrcc.all"
            )}
            exact
            path="/"
          >
            {<OrganizationChart></OrganizationChart>}
          </PrivateRoute>

          <PrivateRoute
            auth={isLogin}
            hasPermissions={validatePermission(
              user ? user.permissions : [],
              "app_organigrama.rrcc.all"
            )}
            exact
            path="/organigramas"
          >
            <OrganizationChart />
          </PrivateRoute>

          <PrivateRoute
            auth={isLogin}
            hasPermissions={validatePermission(
              user ? user.permissions : [],
              "app_organigrama.rrcc.all"
            )}
            exact
            path="/organigramas/organigrama-actual/:company_id"
          >
            <CurrentOrgChart></CurrentOrgChart>
          </PrivateRoute>

          <PrivateRoute
            auth={isLogin}
            hasPermissions={validatePermission(
              user ? user.permissions : [],
              "app_organigrama.rrcc.all"
            )}
            exact
            path="/organigramas/borradores/:company_id"
          >
            <ShowDrafts />
          </PrivateRoute>

          <PrivateRoute
            auth={isLogin}
            hasPermissions={validatePermission(
              user ? user.permissions : [],
              "app_organigrama.rrcc.all"
            )}
            exact
            path="/organigramas/borradores/:company_id/:template_id"
          >
            <EditDraftOrgChart />
          </PrivateRoute>

          <PrivateRoute
            auth={isLogin}
            hasPermissions={validatePermission(
              user ? user.permissions : [],
              "app_organigrama.rrcc.all"
            )}
            exact
            path="/organigramas/historial/:company_id"
          >
            <HistoryOrganizationChart />
          </PrivateRoute>

          <PrivateRoute
            auth={isLogin}
            hasPermissions={validatePermission(
              user ? user.permissions : [],
              "app_organigrama.rrcc.all"
            )}
            exact
            path="/organigramas/historial/:company_id/:id_mongo&version=:version"
          >
            <ShowOrgChartHistory />
          </PrivateRoute>

          <Route exact path="/organigrama" component={Tab} />
          <Route exact path="/organigrama/:id" component={OrgChartCompany} />
          <Route
            exact
            path="/organigrama/company/:id/employee/:employee"
            component={OrgChartCompany}
          />

          <Route
            exact
            path="/organigrama/departamento/:id"
            component={OrgChartDepartment}
          />
          <Route
            exact
            path="/organigrama/departamento/:id/:employee"
            component={OrgChartDepartment}
          />

          <Route
            exact
            path="/organigrama/etiqueta/:id"
            component={OrganigramaMainTag}
          />

          <Route
            exact
            path="/organigrama/etiqueta/:id/:employee"
            component={OrganigramaMainTag}
          />

          <Route
            exact
            path="/organigrama/:path/:id/empleado/:id_employee"
            component={OrgChartDepartment}
          />

          <PrivateRoute
            auth={isLogin}
            hasPermissions={validatePermission(
              user ? user.permissions : [],
              "app_organigrama.rrcc.all"
            )}
            exact
            path="/gestion-empleados"
          >
            <EmployeeManagementMain />
          </PrivateRoute>
        </Switch>
        </Suspense>
      </div>
    </div>
  );
};

const PrivateRoute = (props: any) => {
  if (props.auth) {
    if (props.hasPermissions) {
      return <Route {...props}></Route>;
    } else {
      return <Redirect to="/organigrama" />;
    }
  } else {
    return <Route {...props}></Route>;
  }
};
